import { BugoBrandBrief } from '@shared/api/bugoBrand/bugoBrand.interface';

/**
 * 상조(부고브랜드) 표기명.
 * 멀티 브랜드를 쓰는 상조는 fevent.bugoBrandAgencyIndex가 가리키는 bugoAgencyList 항목이 실제 표기명이다.
 * populate 안된(문자열 id) 경우엔 빈 문자열.
 */
export const getAgencyBrandName = (
  bugoBrand?: BugoBrandBrief | string | null,
  bugoBrandAgencyIndex?: number,
): string => {
  if (!bugoBrand || typeof bugoBrand === 'string') return '';
  const multiAgency =
    typeof bugoBrandAgencyIndex === 'number'
      ? bugoBrand.bugoAgencyList?.[bugoBrandAgencyIndex]
      : undefined;
  return (
    multiAgency?.nickName ??
    multiAgency?.corpName ??
    bugoBrand.bugoAgency?.nickName ??
    bugoBrand.bugoAgency?.corpName ??
    ''
  );
};

/**
 * 상조 콘솔의 "수익금" 컬럼 라벨 — "{상조 약칭} 수익금".
 * 상조 콘솔은 태넌트마다 다른 회사가 보므로 특정 상조명(프리드 등)을 박으면 안 된다(2026-09-10 효담 실사고).
 * 약칭이 없으면 중립적인 "상조 수익금".
 */
export const agencyProfitLabel = (
  bugoBrand?: Pick<BugoBrandBrief, 'bugoAgency'> | null,
): string => {
  //* nickName이 "웅진프리드라이프"처럼 길면 컬럼이 넘치므로 표용 약칭(shortBrandName)을 거친다 → "웅진프리드 수익금"
  const name = shortBrandName(bugoBrand?.bugoAgency?.nickName?.trim());
  return name ? `${name} 수익금` : '상조 수익금';
};

/**
 * 표에 넣을 짧은 상조명.
 * 서버가 주는 표기명은 이미 nickName 우선(= getAgencyBrandName과 같은 규칙)이지만,
 * "웅진프리드라이프"처럼 그래도 긴 이름이 있어 표에서는 잘라 쓴다.
 * **자른 건 반드시 title로 전체명을 같이 준다** — 상조를 잘못 짚으면 응대가 통째로 틀어진다.
 */
const BRAND_NICKNAME: Record<string, string> = {
  웅진프리드라이프: '웅진프리드',
  프리드라이프: '프리드',
  대명스테이션: '대명',
  교원라이프: '교원',
  보람상조라이프: '보람',
  더케이예다함상조: '예다함',
};

export const shortBrandName = (name?: string, max = 6): string => {
  if (!name) return '';
  const alias = BRAND_NICKNAME[name.replace(/\s/g, '')];
  if (alias) return alias;
  return name.length > max ? `${name.slice(0, max)}…` : name;
};

/** CS 로그 주문 요약에서 상조명을 뽑을 때 필요한 최소 형태 */
export interface OrderBrandSource {
  /** 서버가 표기명을 확정해 내려준 경우 */
  bugoBrandName?: string;
  /** 그냥 populate만 해준 경우 (order.bugoBrand ref — 두 주문 스키마 모두 보유) */
  bugoBrand?: BugoBrandBrief | string | null;
  /** 멀티 브랜드 상조에서 실제 표기명을 가리키는 인덱스 (fevent 경유) */
  bugoBrandAgencyIndex?: number;
}

/**
 * 주문 요약 → 상조 표기명.
 * 서버가 `bugoBrandName`으로 확정해 주면 그걸 쓰고, 그냥 `bugoBrand`를 populate만 해줘도
 * 프론트가 같은 규칙(getAgencyBrandName)으로 조립한다 — 어느 쪽이 오든 화면은 동일.
 */
export const orderBrandName = (order?: OrderBrandSource | null): string =>
  order?.bugoBrandName ??
  getAgencyBrandName(order?.bugoBrand, order?.bugoBrandAgencyIndex);
